import React, { useState, useRef, useEffect } from "react";
import { FiPlay, FiSquare, FiRotateCw, FiCheckCircle, FiAlertCircle } from "react-icons/fi";
import toast from "react-hot-toast";
import api from "@/services/api";

/**
 * Enqueues AI screening for candidates and polls until the cron drains the queue.
 *
 * Server-side endpoint /application/screening/run skips candidates that are already
 * `done` AND match the job's current prompt version, so re-clicking Run doesn't burn
 * Claude credits on unchanged work. Toggle "Force" to re-score everything.
 *
 * Props:
 *   filter          – { job_id, status } for the screening endpoints
 *   applicationIds  – explicit list of application _ids (used when selectAll=false)
 *   selectAll       – when true, scope to filter; otherwise scope to applicationIds
 *   onDone          – called on progress and on completion (to refresh the table)
 */
const POLL_INTERVAL_MS = 2500;

export default function ScreeningBulkRun({ filter, applicationIds = [], selectAll, onDone }) {
  const [state, setState] = useState("idle"); // idle | enqueuing | polling | done | stopped
  const [progress, setProgress] = useState({ current: 0, total: 0, errors: 0 });
  const [force, setForce] = useState(false);
  const abortRef = useRef(false);
  const pollTimerRef = useRef(null);
  const enqueuedIdsRef = useRef([]);
  const lastDoneRef = useRef(0);

  useEffect(() => () => clearTimeout(pollTimerRef.current), []);

  const scopeBody = () => (selectAll ? { filter } : { application_ids: applicationIds });
  const pollScopeBody = () =>
    enqueuedIdsRef.current.length > 0 ? { application_ids: enqueuedIdsRef.current } : scopeBody();

  const pollOnce = async () => {
    if (abortRef.current) return;
    try {
      const res = await api.post(`/application/screening/status`, pollScopeBody());
      if (!res?.ok) return;
      const counts = res.counts || {};
      const done = (counts.done || 0) + (counts.error || 0);
      const errors = counts.error || 0;
      const stillRunning = (counts.pending || 0) + (counts.in_progress || 0);

      setProgress((p) => ({ ...p, current: done, errors }));

      if (done > lastDoneRef.current && onDone) {
        onDone();
        lastDoneRef.current = done;
      }

      if (stillRunning === 0) {
        setState("done");
        return;
      }
    } catch (e) {
      // network blip — keep polling
    }
    pollTimerRef.current = setTimeout(pollOnce, POLL_INTERVAL_MS);
  };

  const run = async () => {
    setState("enqueuing");
    abortRef.current = false;
    enqueuedIdsRef.current = [];
    lastDoneRef.current = 0;
    setProgress({ current: 0, total: 0, errors: 0 });

    try {
      const res = await api.post("/application/screening/run", { ...scopeBody(), force });
      if (!res?.ok) {
        toast.error("Failed to queue screening");
        setState("idle");
        return;
      }

      const { enqueued, skipped_current, skipped_queued } = res;
      enqueuedIdsRef.current = res.enqueued_ids || [];

      if (enqueued === 0) {
        const msg =
          skipped_current > 0
            ? `All ${skipped_current} candidate${skipped_current === 1 ? "" : "s"} already scored with current prompt`
            : skipped_queued > 0
            ? `${skipped_queued} already in queue`
            : "Nothing to score";
        toast.success(msg);
        setState("done");
        setProgress({ current: 0, total: 0, errors: 0 });
        if (onDone) onDone();
        return;
      }

      if (skipped_current > 0) {
        toast.success(`Queued ${enqueued} · skipped ${skipped_current} already current`);
      }
      setProgress({ current: 0, total: enqueued, errors: 0 });
      setState("polling");
      if (onDone) onDone();
      pollOnce();
    } catch (e) {
      console.error(e);
      toast.error("Failed to queue screening");
      setState("idle");
    }
  };

  const stop = async () => {
    abortRef.current = true;
    clearTimeout(pollTimerRef.current);
    try {
      await api.post(`/application/screening/cancel`, pollScopeBody());
    } catch (e) {
      // best-effort
    }
    setState("stopped");
    if (onDone) onDone();
  };

  const reset = () => {
    setState("idle");
    setProgress({ current: 0, total: 0, errors: 0 });
    enqueuedIdsRef.current = [];
    lastDoneRef.current = 0;
  };

  const label = "AI Scoring";
  const pct = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

  if (state === "idle") {
    return (
      <div className="flex items-center gap-2">
        <button
          onClick={run}
          title={`Run ${label} on ${selectAll ? "all" : applicationIds.length} candidate${applicationIds.length !== 1 ? "s" : ""}${force ? " (force re-run)" : ""}`}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white/10 text-white hover:bg-white/20 rounded-lg transition-colors"
        >
          <FiPlay className="w-3 h-3" />
          Run {label}
        </button>
        <label className="flex items-center gap-1 text-xs text-white/70 hover:text-white cursor-pointer select-none">
          <input
            type="checkbox"
            checked={force}
            onChange={(e) => setForce(e.target.checked)}
            className="rounded border-white/30 bg-transparent text-indigo-500 focus:ring-0 focus:ring-offset-0 w-3 h-3"
          />
          Force
        </label>
      </div>
    );
  }

  if (state === "enqueuing" || state === "polling") {
    const subLabel = state === "enqueuing" ? "Queuing…" : label;
    return (
      <div className="flex items-center gap-3">
        <span className="text-xs font-medium text-white/70">{subLabel}</span>
        <div className="flex items-center gap-2 min-w-[160px]">
          <div className="flex-1 h-1.5 bg-white/20 rounded-full overflow-hidden">
            <div className="h-full bg-white rounded-full transition-all duration-300" style={{ width: `${pct}%` }} />
          </div>
          <span className="text-xs font-medium text-white/90 whitespace-nowrap">
            {progress.current}/{progress.total}
          </span>
          {progress.errors > 0 && (
            <span className="text-xs font-medium text-red-300 whitespace-nowrap">({progress.errors} err)</span>
          )}
        </div>
        <button
          onClick={stop}
          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-300 hover:text-red-200 hover:bg-white/10 rounded-lg transition-colors"
        >
          <FiSquare className="w-3 h-3" />
          Stop
        </button>
      </div>
    );
  }

  // done or stopped
  return (
    <div className="flex items-center gap-2">
      {state === "done" ? (
        <span className="flex items-center gap-1 text-xs font-medium text-emerald-300">
          <FiCheckCircle className="w-3.5 h-3.5" />
          {label} — {progress.current}/{progress.total}
          {progress.errors > 0 && <span className="text-red-300 ml-1">({progress.errors} err)</span>}
        </span>
      ) : (
        <span className="flex items-center gap-1 text-xs font-medium text-amber-300">
          <FiAlertCircle className="w-3.5 h-3.5" />
          {label} — stopped {progress.current}/{progress.total}
        </span>
      )}
      <button
        onClick={reset}
        title="Reset"
        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
      >
        <FiRotateCw className="w-3 h-3" />
      </button>
      <button
        onClick={run}
        title={`Re-run ${label}`}
        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-white/90 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
      >
        <FiPlay className="w-3 h-3" />
        Retry
      </button>
    </div>
  );
}
